<template>
    <!--
        NOTE:

        To use this, you need to create a method inside your component (e.g. openToast)
        wherein you'll be using event bus to pass data:

        openToast() {
            this.$eventBus.$emit('trigger-toast', {
                'actionEvent': {
                    name: [Insert name of event bus callback],
                    payload: { Insert data to send }
                },
                'actionLabel': [Insert the label of your action button here],
                'isError': [Set this to TRUE if the toast indicates an error],
                'isLoading': [Set this to TRUE if the toast indicates a loading state],
                'label': [Insert the toast message here]
            });
        }

        'actionEvent'   -   optional; should contain the event that will trigger your action
        'actionLabel'   -   optional; use a concise action label that's related to your toast message (e.g. View, Undo)
        'isError'       -   optional; set this to TRUE if the toast indicates an error
        'isLoading'     -   optional; set this to TRUE if the toast indicates a loading state
        'label'         -   required; keep the label short and concise

        ***

        You can also check App.vue to know how the Toast component is declared.
    -->
    <div :class="[ 'feedback-toast', { 'red' : error } ]">
        <div class="text-container">
            <Loading
                v-if="isLoading"
                color="white"
                size="small"
            />
            <i
                v-else
                :class="`mdi mdi-24px mdi-${ error ? 'alert-circle' : 'check-circle c-success' }`"
            />
            <p> {{ label }} </p>
        </div>
        <div
            v-if="actionLabel || !isLoading"
            class="actions-container"
        >
            <Button
                v-if="actionLabel"
                class="transparent tertiary action"
                @click="$emit('action')"
            >
                <p class="semibold">
                    {{ actionLabel }}
                </p>
            </Button>
            <template v-if="!isLoading">
                <CloseButton
                    v-if="windowWidth > MOBILE"
                    @close="$emit('close')"
                />
                <Button
                    v-else
                    class="transparent tertiary action"
                    @click="$emit('close')"
                >
                    Dismiss
                </Button>
            </template>
        </div>
    </div>
</template>

<script>
    import Button from 'source/components/_generics/Button.vue';
    import CloseButton from 'source/components/_generics/CloseButton.vue';
    import Loading from 'source/components/_generics/Loading.vue';

    export default {
        name: 'FeedbackToast',

        components: {
            Button,
            CloseButton,
            Loading,
        },

        props: {
            actionLabel: {
                type: String,
                required: false
            },

            // NOTE: Deprecated
            color: {
                type: String,
                required: false
            },

            isError: {
                type: Boolean,
                required: false,
                default: false
            },

            isLoading: {
                type: Boolean,
                required: false,
                default: false
            },

            label: {
                type: String,
                required: true
            }
        },

        emits: ['action', 'close'],

        data() {
            return {
                // Responsiveness
                MOBILE: 576,
                windowWidth: 0
            };
        },

        computed: {
            error() {
                return !this.isLoading && (this.isError || this.color === 'red');
            }
        },

        created() {
            // get window width
            window.addEventListener('resize', this.handleResize);
            this.handleResize();
        },

        unmounted() {
            window.removeEventListener('resize', this.handleResize);
        },

        mounted() {
            if (!this.isLoading) {
                setTimeout(() => {
                    this.$emit('close');
                }, 5000);
            }
        },

        methods: {
            handleResize() {
                this.windowWidth = window.innerWidth;
            }
        }
    };
</script>

<style lang="scss" scoped>
    @import 'source/stylesheets/sass/abstract/bpo-variables.scss';
    $error-dark: var(--error-hover);

    .feedback-toast {
        @include flex-align-center;
        gap: 40px;
        height: 48px;
        min-width: 360px;
        width: max-content;
        padding-inline: 16px;
        border-radius: 8px;
        background: $secondary;
        color: $white;
        position: fixed;
        bottom: 48px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 999;

        @media #{$mobile} {
            flex-direction: column;
            align-items: unset;
            gap: 12px;
            height: unset;
            min-width: 0;
            width: calc(100% - 32px);
            padding-block: 16px;

            .actions-container {
                gap: unset;
                margin-left: auto;
            }
        }

        &.red {
            background: $error-dark;

            .actions-container {
                :deep(button.action:hover) {
                    color: $error-dark;
                }
            }
        }

        .text-container {
            @include flex-align-center;
            gap: 10px;
            flex: 1;
        }

        .actions-container {
            @include flex-align-center;
            gap: 4px;

            :deep(button) {
                color: $white;

                &.action {
                    height: 28px;
                    padding-inline: 8px;

                    &:hover {
                        color: $secondary;
                    }
                }

                &.close {
                    @include width-height(28px);
                    transition-duration: .2s;

                    &:hover {
                        color: $error-dark;
                        background: $white !important;
                    }

                    i {
                        margin-top: 3px;
                    }
                }
            }
        }
    }

    .fade-enter-active, .fade-leave-active {
        transition: bottom 0.3s ease-out, opacity 0.3s ease-out;
    }

    .fade-enter-from, .fade-leave-to {
        bottom: 0;
        opacity: 0;
    }
</style>
